import { FUNDING_STATUSES, STAGES, type FundingStatus, type ProjectListItem, type Stage } from '../../api/types'

export type SortKey = 'stage' | 'valueDesc' | 'valueAsc' | 'name' | 'followUp' | 'starred'

export const SORT_LABELS: Record<SortKey, string> = {
  stage: 'Sort: by stage',
  valueDesc: 'Sort: value (high to low)',
  valueAsc: 'Sort: value (low to high)',
  name: 'Sort: name',
  followUp: 'Sort: next follow-up',
  starred: 'Sort: starred first',
}

export type PipelineFilters = {
  stages: Stage[]
  departmentId: number | null
  funding: FundingStatus | null
  state: string | null
  category: string | null
  createdById: number | null
  search: string
  sort: SortKey
  overdueOnly: boolean
  showArchived: boolean
}

export const DEFAULT_FILTERS: PipelineFilters = {
  stages: [...STAGES],
  departmentId: null,
  funding: null,
  state: null,
  category: null,
  createdById: null,
  search: '',
  sort: 'stage',
  overdueOnly: false,
  showArchived: false,
}

/**
 * Pipeline addresses can carry filters, e.g. /pipeline?stage=Lead&state=Karnataka, so other screens
 * (the Dashboard tiles) can open it already filtered. Unknown or invalid values are ignored.
 */
export function pipelineLink(f: {
  stages?: Stage[]
  departmentId?: number | null
  funding?: FundingStatus | null
  state?: string | null
  category?: string | null
  createdById?: number | null
  overdueOnly?: boolean
}): string {
  const q = new URLSearchParams()
  if (f.stages && f.stages.length < STAGES.length) q.set('stage', f.stages.join(','))
  if (f.departmentId != null) q.set('department', String(f.departmentId))
  if (f.funding) q.set('funding', f.funding)
  if (f.state) q.set('state', f.state)
  if (f.category) q.set('category', f.category)
  if (f.createdById != null) q.set('createdBy', String(f.createdById))
  if (f.overdueOnly) q.set('overdue', '1')
  const query = q.toString()
  return query ? `/pipeline?${query}` : '/pipeline'
}

/** The filters described by a Pipeline address (everything else stays at its default). */
export function filtersFromQuery(q: URLSearchParams): PipelineFilters {
  const id = (key: string) => {
    const n = Number(q.get(key))
    return Number.isInteger(n) && n > 0 ? n : null
  }
  const stages = (q.get('stage') ?? '').split(',').filter((s): s is Stage => (STAGES as readonly string[]).includes(s))
  const funding = q.get('funding')
  return {
    ...DEFAULT_FILTERS,
    stages: stages.length > 0 ? stages : [...STAGES],
    departmentId: id('department'),
    funding: funding && (FUNDING_STATUSES as readonly string[]).includes(funding) ? (funding as FundingStatus) : null,
    state: q.get('state') || null,
    category: q.get('category') || null,
    createdById: id('createdBy'),
    overdueOnly: q.get('overdue') === '1',
  }
}

/** Keeps the projects that pass every filter, then sorts them. */
export function applyFilters(projects: ProjectListItem[], f: PipelineFilters): ProjectListItem[] {
  const search = f.search.trim().toLowerCase()

  const kept = projects.filter(
    (p) =>
      f.stages.includes(p.stage) &&
      (f.departmentId === null || p.departmentId === f.departmentId) &&
      (f.funding === null || p.fundingStatus === f.funding) &&
      (f.state === null || p.state === f.state) &&
      (f.category === null || p.category === f.category) &&
      (f.createdById === null || p.createdById === f.createdById) &&
      (!f.overdueOnly || p.isOverdue) &&
      (f.showArchived || !p.isArchived) &&
      (search === '' || matchesSearch(p, search)),
  )

  return kept.sort(comparers[f.sort])
}

/** Search across project title, department and stakeholder names. */
function matchesSearch(p: ProjectListItem, search: string): boolean {
  return [p.title, p.departmentName, ...p.stakeholderNames].some((text) =>
    text.toLowerCase().includes(search),
  )
}

type Comparer = (a: ProjectListItem, b: ProjectListItem) => number

const byStage: Comparer = (a, b) => STAGES.indexOf(a.stage) - STAGES.indexOf(b.stage)
const byValueDesc: Comparer = (a, b) => b.valueRupees - a.valueRupees
const byName: Comparer = (a, b) => a.title.localeCompare(b.title)

/** Earliest follow-up first; projects without a date go last. */
const byFollowUp: Comparer = (a, b) => {
  if (a.nextFollowUp === b.nextFollowUp) return 0
  if (a.nextFollowUp === null) return 1
  if (b.nextFollowUp === null) return -1
  return a.nextFollowUp < b.nextFollowUp ? -1 : 1 // "YYYY-MM-DD" strings sort like dates
}

/** Use the first comparer that tells two projects apart. */
const thenBy =
  (...comparers: Comparer[]): Comparer =>
  (a, b) => {
    for (const compare of comparers) {
      const result = compare(a, b)
      if (result !== 0) return result
    }
    return 0
  }

/** Order the projects were added (ids only ever go up). */
const byAdded: Comparer = (a, b) => a.id - b.id

const comparers: Record<SortKey, Comparer> = {
  stage: thenBy(byStage, byAdded),
  valueDesc: thenBy(byValueDesc, byName),
  valueAsc: thenBy((a, b) => a.valueRupees - b.valueRupees, byName),
  name: byName,
  followUp: thenBy(byFollowUp, byStage),
  starred: thenBy((a, b) => Number(b.isStarred) - Number(a.isStarred), byStage, byValueDesc),
}

/** Distinct, sorted, non-empty values for a filter dropdown. */
export function distinct(values: (string | null)[]): string[] {
  return [...new Set(values.filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b))
}
